import { FormEvent, useMemo, useState } from 'react';
import { Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { TableColumn } from '@/types';
import { dateWithMonthName } from '@/utils/formatters';
import {
  useCreateFlussonicServer,
  useDeleteFlussonicServer,
  useFlussonicServers,
  useUpdateFlussonicServer,
} from '@/hooks/useFlussonicServers';
import { FlussonicServer } from '@/services/flussonicServerService';

interface ServerFormState {
  name: string;
  baseUrl: string;
  username: string;
  password: string;
  enabled: boolean;
  notes: string;
}

const emptyForm: ServerFormState = {
  name: '',
  baseUrl: '',
  username: '',
  password: '',
  enabled: true,
  notes: '',
};

const TVChannelsFlussonicServers = () => {
  const navigate = useNavigate();
  const { data: servers = [], isLoading, isError, error, refetch, isFetching } = useFlussonicServers();
  const createServerMutation = useCreateFlussonicServer();
  const updateServerMutation = useUpdateFlussonicServer();
  const deleteServerMutation = useDeleteFlussonicServer();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<FlussonicServer | null>(null);
  const [form, setForm] = useState<ServerFormState>(emptyForm);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isSubmitting = createServerMutation.isPending || updateServerMutation.isPending;

  const sortedServers = useMemo(
    () => [...servers].sort((a, b) => a.name.localeCompare(b.name)),
    [servers]
  );

  const openCreateModal = () => {
    setEditingServer(null);
    setForm(emptyForm);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (server: FlussonicServer) => {
    setEditingServer(server);
    setForm({
      name: server.name,
      baseUrl: server.baseUrl,
      username: server.username,
      password: '',
      enabled: server.enabled,
      notes: server.notes || '',
    });
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    setIsModalOpen(false);
    setEditingServer(null);
    setForm(emptyForm);
    setSubmitError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    try {
      if (editingServer) {
        await updateServerMutation.mutateAsync({
          id: editingServer.id,
          name: form.name,
          baseUrl: form.baseUrl,
          username: form.username,
          password: form.password.trim() ? form.password : undefined,
          enabled: form.enabled,
          notes: form.notes,
        });
      } else {
        await createServerMutation.mutateAsync({
          name: form.name,
          baseUrl: form.baseUrl,
          username: form.username,
          password: form.password,
          enabled: form.enabled,
          notes: form.notes,
        });
      }

      closeModal();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'No se pudo guardar el servidor';
      setSubmitError(message);
    }
  };

  const handleDelete = async (server: FlussonicServer) => {
    const confirmed = window.confirm(`¿Eliminar servidor ${server.name}?`);
    if (!confirmed) {
      return;
    }

    try {
      await deleteServerMutation.mutateAsync(server.id);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'No se pudo eliminar el servidor');
    }
  };

  const columns: TableColumn<FlussonicServer>[] = [
    {
      key: 'name',
      header: 'Nombre',
      accessor: (server) => <span className="font-medium text-gray-900">{server.name}</span>,
    },
    {
      key: 'baseUrl',
      header: 'URL Base',
      accessor: (server) => (
        <span className="block max-w-md truncate text-sm text-gray-900" title={server.baseUrl}>
          {server.baseUrl}
        </span>
      ),
    },
    {
      key: 'username',
      header: 'Usuario',
      accessor: (server) => <span className="text-sm text-gray-900">{server.username}</span>,
    },
    {
      key: 'enabled',
      header: 'Estado',
      accessor: (server) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          server.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
        }`}>
          {server.enabled ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'updatedDate',
      header: 'Actualizado',
      accessor: (server) => (
        <span className="text-sm text-gray-900">
          {dateWithMonthName(server.updatedDate, 'No Disponible')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'center',
      accessor: (server) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/content/tv-channels/flussonic-servers/${server.id}`)}
          >
            Detalles
          </Button>
          <Button size="sm" variant="outline" onClick={() => openEditModal(server)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDelete(server)}
            disabled={deleteServerMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        {isError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Error al cargar servidores'}
          </div>
        )}

        {isLoading ? (
          <div className="border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow">
            Cargando servidores...
          </div>
        ) : (
          <DataTable
            data={sortedServers}
            columns={columns}
            paginate={false}
            tableStyle="compact"
            showToolbar={false}
            className="shadow"
            headerRenderer={() => (
              <div className="border-b border-gray-200 bg-white">
                <div className="flex flex-col gap-3 px-4 py-2 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Servidores Flussonic</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetch()}
                      disabled={isFetching}
                    >
                      <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                      Refrescar
                    </Button>
                    <Button size="sm" onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      <Plus className="mr-1 h-4 w-4" />
                      Nuevo servidor
                    </Button>
                  </div>
                </div>
              </div>
            )}
          />
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={editingServer ? 'Editar servidor Flussonic' : 'Nuevo servidor Flussonic'}
          size="xl"
          closeOnBackdrop={!isSubmitting}
          closeOnEsc={!isSubmitting}
        >
          <form onSubmit={handleSubmit} className="space-y-4 px-2 py-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                required
                disabled={isSubmitting}
              />
              <Input
                label="URL Base API"
                value={form.baseUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, baseUrl: e.target.value }))}
                placeholder="https://flussonic.example.com"
                required
                disabled={isSubmitting}
              />
              <Input
                label="Usuario"
                value={form.username}
                onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                required
                disabled={isSubmitting}
              />
              <Input
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                required={!editingServer}
                placeholder={editingServer ? 'Dejar vacío para mantener la actual' : undefined}
                disabled={isSubmitting}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                disabled={isSubmitting}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Servidor activo
            </label>

            <div>
              <label htmlFor="flussonic-notes" className="mb-1 block text-sm font-medium text-gray-700">
                Notas
              </label>
              <textarea
                id="flussonic-notes"
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                disabled={isSubmitting}
                rows={4}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            {submitError && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
              <Button type="button" variant="outline" onClick={closeModal} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelsFlussonicServers;
